import { useEffect, useRef, useState } from "react";
import { BACKEND_URL, api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { AvatarIcon, CountryFlag } from "@/lib/identity";
import { PremiumBadge } from "@/components/PremiumBadge";
import { CalendarStar, CheckCircle, XCircle, Handshake } from "@phosphor-icons/react";
import { toast } from "sonner";

const QUESTION_SECONDS = 15;

function fmtCountdown(ms) {
  if (ms <= 0) return "starting…";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  if (d) return `${d}d ${h}h`;
  if (h) return `${h}h ${m}m`;
  if (m) return `${m}m ${s}s`;
  return `${s}s`;
}

export default function Saturday() {
  const { user } = useAuth();
  const [show, setShow] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [phase, setPhase] = useState("WAITING"); // WAITING | LIVE | REVEAL | ENDED
  const [activeQ, setActiveQ] = useState(null);
  const [pick, setPick] = useState(null);
  const [reveal, setReveal] = useState(null);
  const [secLeft, setSecLeft] = useState(QUESTION_SECONDS);
  const [countdown, setCountdown] = useState(0);
  const wsRef = useRef(null);

  useEffect(() => {
    api.get("/saturday/state").then(({ data }) => {
      setShow(data.show);
      setLeaderboard(data.leaderboard || []);
      if (data.show?.state === "ENDED") setPhase("ENDED");
      else if (data.show?.state === "RUNNING") setPhase("LIVE");
    }).catch(() => {});
    const token = localStorage.getItem("nk_session_token");
    if (!token) return;
    const wsUrl = `${BACKEND_URL.replace(/^http/, "ws")}/api/saturday/ws?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;
    ws.onmessage = (e) => {
      try { handleMsg(JSON.parse(e.data)); } catch { /* */ }
    };
    return () => { try { ws.close(); } catch { /* */ } };
  }, []);

  // Countdown until show starts
  useEffect(() => {
    if (!show?.scheduled_for || show.state !== "SCHEDULED") return;
    const tick = () => setCountdown(new Date(show.scheduled_for).getTime() - Date.now());
    tick(); const id = setInterval(tick, 1000); return () => clearInterval(id);
  }, [show]);

  // Per-question timer
  useEffect(() => {
    if (phase !== "LIVE" || !activeQ?.started_at) return;
    const start = new Date(activeQ.started_at).getTime();
    const tick = () => setSecLeft(Math.max(0, QUESTION_SECONDS - (Date.now() - start) / 1000));
    tick(); const id = setInterval(tick, 100); return () => clearInterval(id);
  }, [phase, activeQ]);

  const handleMsg = (msg) => {
    if (msg.type === "snapshot") {
      const s = msg.data?.show;
      setShow(s);
      setLeaderboard(msg.data?.leaderboard || []);
      if (s?.state === "RUNNING") setPhase("LIVE");
      else if (s?.state === "ENDED") setPhase("ENDED");
    } else if (msg.type === "show_started") {
      setPhase("LIVE");
      setShow((sh) => sh ? { ...sh, state: "RUNNING" } : sh);
    } else if (msg.type === "question_started") {
      setActiveQ(msg.data); setPick(null); setReveal(null); setPhase("LIVE");
    } else if (msg.type === "question_ended") {
      setReveal({ correct_index: msg.data.correct_index, winner_username: msg.data.winner_username });
      setPhase("REVEAL");
    } else if (msg.type === "show_ended") {
      setPhase("ENDED");
      // Refresh leaderboard
      api.get("/saturday/leaderboard").then(({ data }) => setLeaderboard(data.entries || [])).catch(() => {});
    }
  };

  const submit = async (i) => {
    if (pick !== null || !activeQ) return;
    setPick(i);
    try {
      await api.post("/saturday/answer", {
        show_id: show.show_id, question_index: activeQ.index, option_index: i,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
      setPick(null);
    }
  };

  return (
    <div className="space-y-8">
      <SponsorBanner show={show} />

      <header>
        <p className="eyebrow mb-2 text-nk-gold">Weekly · Saturdays at {(show?.scheduled_for ? new Date(show.scheduled_for) : new Date()).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</p>
        <h1 className="font-display text-3xl sm:text-4xl font-medium tracking-tight">
          {show?.sponsor_name
            ? /saturday|challenge/i.test(show.sponsor_name)
              ? show.sponsor_name
              : `The ${show.sponsor_name} Saturday Challenge`
            : "NK Saturday Challenge"}
        </h1>
        <p className="text-nk-mute text-sm mt-3 max-w-2xl">
          30 questions, live, every Saturday. Special leaderboard. 2× rewards: <span className="font-mono">+20 SS</span> per correct, <span className="font-mono">+50 SS</span> bonus for fastest.
        </p>
      </header>

      {/* Countdown when scheduled */}
      {show?.state === "SCHEDULED" && (
        <div className="nk-card p-6 sm:p-8 text-center border-nk-gold/30 bg-nk-gold/[0.03]">
          <CalendarStar size={28} weight="duotone" className="text-nk-gold mx-auto mb-3" />
          <p className="eyebrow mb-2 text-nk-gold">Starts in</p>
          <p className="font-display text-4xl sm:text-5xl font-medium">{fmtCountdown(countdown)}</p>
          <p className="text-sm text-nk-mute mt-3">{new Date(show.scheduled_for).toLocaleString()}</p>
        </div>
      )}

      {/* Live question */}
      {phase === "LIVE" && activeQ && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="eyebrow">Q {activeQ.index + 1} / {activeQ.total}</p>
            <span className={`text-sm font-mono ${secLeft < 5 ? "text-nk-bad" : ""}`}>{Math.ceil(secLeft)}s</span>
          </div>
          <div className="h-1 bg-nk-line rounded-full overflow-hidden">
            <div className={`h-full ${secLeft < 5 ? "bg-nk-bad" : "bg-nk-gold"} transition-all`} style={{ width: `${(secLeft / QUESTION_SECONDS) * 100}%` }} />
          </div>
          <div className="nk-card p-5 sm:p-7">
            <p className="text-[11px] text-nk-dim font-mono mb-3 uppercase tracking-wider">{activeQ.category}</p>
            <p className="font-display text-xl sm:text-2xl font-medium leading-snug mb-6">{activeQ.body}</p>
            <div className="grid gap-2.5 sm:gap-3">
              {activeQ.options.map((opt, i) => (
                <button
                  key={i}
                  data-testid={`saturday-option-${i}`}
                  onClick={() => submit(i)}
                  disabled={pick !== null}
                  className={`text-left border rounded-sm px-4 py-3 transition-all flex items-center gap-3 ${
                    pick === i ? "border-nk-gold bg-nk-gold/10" :
                    pick !== null ? "border-nk-line text-nk-dim" :
                    "border-nk-line hover:border-nk-gold hover:bg-nk-gold/5"
                  }`}
                >
                  <span className="font-mono text-xs text-nk-dim w-4">{String.fromCharCode(65 + i)}</span>
                  <span className="flex-1 text-sm sm:text-base">{opt}</span>
                  {pick === i && <CheckCircle size={16} weight="fill" className="text-nk-gold" />}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Reveal */}
      {phase === "REVEAL" && activeQ && reveal && (
        <section className="nk-card p-5">
          <p className="eyebrow mb-3">Reveal · Q {activeQ.index + 1}</p>
          <div className="space-y-2">
            {activeQ.options.map((opt, i) => {
              const correct = i === reveal.correct_index;
              const yours = i === pick;
              return (
                <div key={i} className={`px-4 py-2.5 rounded-sm border flex items-center gap-3 text-sm ${
                  correct ? "border-nk-accent bg-nk-accent/10" :
                  yours ? "border-nk-bad/60 bg-nk-bad/5" : "border-nk-line opacity-70"
                }`}>
                  <span className="font-mono text-xs text-nk-dim w-4">{String.fromCharCode(65 + i)}</span>
                  <span className="flex-1">{opt}</span>
                  {correct && <CheckCircle size={14} weight="fill" className="text-nk-accent" />}
                  {yours && !correct && <XCircle size={14} weight="fill" className="text-nk-bad" />}
                </div>
              );
            })}
          </div>
          {reveal.winner_username && <p className="text-xs text-nk-gold mt-3">Fastest: <b>{reveal.winner_username}</b></p>}
        </section>
      )}

      {/* Leaderboard */}
      <section>
        <p className="eyebrow mb-3">Saturday Leaderboard</p>
        <div className="nk-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-nk-line">
              <tr>
                <Th>#</Th><Th>Player</Th><Th>Country</Th><Th className="text-right">Points</Th>
              </tr>
            </thead>
            <tbody>
              {leaderboard.map((e, i) => (
                <tr key={e.user_id} className={`border-b border-nk-line/40 ${e.user_id === user?.user_id ? "bg-nk-gold/[0.05]" : ""}`}>
                  <td className={`px-4 py-2.5 font-mono ${i < 3 ? "text-nk-gold font-bold" : "text-nk-mute"}`}>#{e.rank || i + 1}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <AvatarIcon id={e.avatar} size={20} />
                      <span>{e.username || "Player"}</span>
                      <PremiumBadge active={e.nk_plus_active} size="sm" />
                    </div>
                  </td>
                  <td className="px-4 py-2.5"><CountryFlag country={e.country} size={12} /></td>
                  <td className="px-4 py-2.5 text-right font-mono text-nk-gold">{(e.points || 0).toLocaleString()}</td>
                </tr>
              ))}
              {leaderboard.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-nk-mute text-sm">No entries yet — be the first to answer when the show starts.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function SponsorBanner({ show }) {
  if (!show?.sponsor_name) return null;
  return (
    <div
      data-testid="saturday-sponsor-banner"
      className="nk-card p-4 sm:p-5 border-nk-gold/40 bg-nk-gold/[0.04] flex items-center gap-4"
    >
      {show.sponsor_logo_url ? (
        <img src={show.sponsor_logo_url.startsWith("/api/uploads/") ? `${BACKEND_URL}${show.sponsor_logo_url}` : show.sponsor_logo_url} alt={show.sponsor_name}
             className="w-12 h-12 sm:w-14 sm:h-14 object-contain shrink-0" />
      ) : (
        <Handshake size={32} weight="duotone" className="text-nk-gold shrink-0" />
      )}
      <div className="min-w-0">
        <p className="text-[10px] uppercase tracking-[0.18em] font-mono text-nk-gold mb-0.5">Sponsored by</p>
        <p className="font-display text-xl truncate">{show.sponsor_name}</p>
      </div>
    </div>
  );
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-3 text-left text-[11px] text-nk-mute font-medium uppercase tracking-wider ${className}`}>{children}</th>;
}
